<script setup lang="ts">
import type { Game } from '@game/types'
import { useRewards, useRewardsClient, useBonus, useEventListener, useDevice, useGameSection, useGameSections } from '#imports'
import { isEmpty } from 'es-toolkit/compat'
import type { FlashMessage, TopFlashMessages } from '#types'

const flags = useFeatureFlags()
const route = useRoute()

// Layout Props
const layoutProps = computed(() => {
    return {
        bodyPadding: (route.meta.layoutBodyPadding ?? true),
        backgroundColor: route.meta.layoutBackgroundColor
    }
})

// Hero
const { isLoggedIn, data: user } = useUserInfo()
const { useGetDepositSuccessfulCount } = usePaymentsClient()
const { isFirstDeposit } = await useGetDepositSuccessfulCount({ server: true, lazy: false })
const { data: accountDetails } = useSeamlessWalletAccountDetails()
const { isMobile } = useDevice()
const { imagePreset } = useHeroConfig()
const logger = useLogger()

const fetchPersonalizedContent = computed(() =>
    !!route.meta?.enablePersonalizedGameHero
    && !!flags?.enablePersonalizedLobby
)

// Promotions
const { useGetReminder, useGetPendingReward } = useRewards()
const { useGetPendingBonus } = useBonus()

const pendingReward = useGetPendingReward()

const gameSectionsQuery = await useGameSections({
    lazy: false,
    enabled: fetchPersonalizedContent,
    pageSlug: () => route.meta?.pageSlug as string | undefined,
})

// True only when personalized sections are actually visible (loading or non-empty),
// vs `fetchPersonalizedContent` which only reflects configuration.
const personalizedContentVisible = computed(() =>
    fetchPersonalizedContent.value
    && (gameSectionsQuery.isLoading.value || (gameSectionsQuery.data.value?.length ?? 0) > 0)
)

// Track the first section slug reactively: this layout is persistent across
// SPA navigations, so a const captured at setup is undefined forever when the
// layout was first mounted on a route where personalization was disabled.
const firstSectionSlug = computed(() => gameSectionsQuery.data.value?.[0]?.slug ?? '')
const personalizedGamesSectionQuery = await useGameSection({
    slug: firstSectionSlug,
    page_size: isMobile ? 16 : 30,
    lazy: false,
    enabled: computed(() => fetchPersonalizedContent.value && !!firstSectionSlug.value),
})

const campaignHeroSlug = computed(() =>
    route.meta?.enableCampaignHero ? parseCampaignHeroSlug(route.query.campaign) : undefined
)

const hero = await useHero({
    page: campaignHeroSlug.value ?? (route.meta?.heroPage as string | undefined),
    disableOnMobile: !!route.meta?.disableHeroOnMobile,
})

// SSR fallback: unknown campaign slugs produce an empty response — recover to the default hero.
if (campaignHeroSlug.value
    && isEmpty(hero.data.value)
    && route.meta?.heroPage
    && route.meta.heroPage !== campaignHeroSlug.value) {
    await hero.change({ page: route.meta.heroPage as string })
}

const reminderQuery = useGetReminder({
    enabled: () => !route.query.txId && pendingReward.status.value === 'success' && !pendingReward.data.value?.Promotion,
})

const pendingBonus = useGetPendingBonus({
    enabled: () => !route.query.txId,
})

// Personalized hero game - derived via computed so it evaluates correctly during SSR
// (Vue SSR does not flush watch callbacks, but computed values evaluate during template rendering)
// undefined = loading/not applicable, null = no game (hide hero), Game = show personalized hero
const heroRandomValue = useState<number>('personalized-hero-random', () => Math.random())
const personalizedHeroGame = computed<Game | null | undefined>(() => {
    if (!fetchPersonalizedContent.value) return undefined
    if (personalizedGamesSectionQuery.isLoading.value) return undefined

    const games = personalizedGamesSectionQuery.data.value?.games
    const candidates = games?.slice(0, 5) ?? []
    if (!candidates.length) return null

    return candidates[Math.floor(heroRandomValue.value * candidates.length)]
})

const heroDisplayMode = computed<'service' | 'personalized' | null>(() => {
    const hasHeroData = !isEmpty(hero.data.value)

    // Without personalization: original behavior (hero service only)
    if (!fetchPersonalizedContent.value) {
        return hasHeroData ? 'service' : null
    }

    // Personalized priority:
    // 1. Campaign hero from service (explicit ?campaign= link always wins over personalized)
    if (hasHeroData && (hero.data.value?.campaign || campaignHeroSlug.value)) {
        return 'service'
    }

    // 2. Personal hero from top 5 games
    if (personalizedHeroGame.value) {
        return 'personalized'
    }

    // Still loading personalized games — show personalized skeleton
    if (personalizedHeroGame.value === undefined) {
        return 'personalized'
    }

    // 3. Any hero from service (fallback, only when personalized games resolved to null)
    if (hasHeroData) {
        return 'service'
    }

    return null
})

const showHero = computed(() => {
    if (!route.meta?.heroPage) return false
    if (route.meta?.disableHeroOnMobile && isMobile) return false

    if (heroDisplayMode.value) return true

    // Personalized hero loading state (show skeleton)
    if (fetchPersonalizedContent.value && personalizedHeroGame.value === undefined) {
        return true
    }

    // Logged out with no hero data: show skeleton
    if (!isLoggedIn.value) return true

    return false
})

const showStickyTerms = computed(() => {
    if (!heroDisplayMode.value) return false
    if (!showHero.value) return false
    if (route.meta?.disableHeroOnMobile && isMobile) return false

    if (isLoggedIn.value) {
        if (!route.meta?.termsShown) return false

        return !!(hero.data.value?.short_terms || hero.data.value?.long_terms)
    }

    return !!route.meta?.termsShown
})

const isFirstBrandLoginWithBalance = computed(() =>
    user.value?.nthLoginForBrand === 1 && Number(accountDetails.value?.balance) >= 1
)

const isSoftSignedUpWithBalance = computed(() =>
    user.value?.wasSoftSignedUp && Number(accountDetails.value?.balance) >= 1
)

const isEligibleForAccountCompletion = computed(() =>
    isLoggedIn.value && isFirstDeposit.value && user.value?.isValidated && !isFirstBrandLoginWithBalance.value && !isSoftSignedUpWithBalance.value
)

if (flags?.enablePromotions && isLoggedIn.value && import.meta.client) {
    // Remove promotion if the game with freespins was started from a tile
    useEventListener(document, 'gameStarted', (payload) => {
        const reminder = reminderQuery.data.value?.reminders?.find(r => r?.CombinedGame?.slug === payload.detail)

        if (reminder) {
            reminderQuery.close(reminder)
        }
    })
}

const { data: reclaimableReminder } = usePromotionReclaimableReminder()

// Flash messages
const { data: flashMessages, refresh: refreshFlashMessages } = useFlashMessages()

const hasFlashMessages = computed(() => {
    return filteredFlashMessages.value?.auth?.length || filteredFlashMessages.value?.flash?.length || filteredFlashMessages.value?.global?.length || filteredFlashMessages.value?.bottomScrollDismiss
})

const filteredFlashMessages = computed<TopFlashMessages>(() => {
    if (!flashMessages.value) return {
        auth: [],
        flash: [],
        global: [],
        bottomScrollDismiss: null
    }

    const filterFlashElement = (array: FlashMessage[], key: string): FlashMessage[] => {
        if (!array) return []

        return array.filter(item => item.element !== key)
    }

    const getBottomScrollDismiss = (array: FlashMessage[], key: string): FlashMessage | null => {
        if (!array) return null

        return array.filter(item => item.element === key)?.[0] ?? null
    }

    return {
        flash: filterFlashElement(flashMessages.value.flash, 'Flash/bottom_scroll_dismiss'),
        auth: flashMessages.value.auth,
        global: flashMessages.value.global.filter((globalNotification) => {
            if (!globalNotification.url_pattern) return true

            let match

            try {
                match = route.path.match(globalNotification.url_pattern)
            }
            catch (err) {
                logger.withTag('layout').withTag('default').withTag('filteredMessages').error('Failed to parse regular expression.', err)

                return false
            }

            return !!match
        }),
        bottomScrollDismiss: getBottomScrollDismiss([...flashMessages.value.flash, ...flashMessages.value.auth], 'Flash/bottom_scroll_dismiss'),
    }
})

const { useGetPendingOffersWithPaymentRequirements } = useRewardsClient()

const signupBonusOffers = useGetPendingOffersWithPaymentRequirements({
    enabled: isEligibleForAccountCompletion,
    hasBonus: offers => !!offers.Bundle,
})

const hasSignupBonus = signupBonusOffers.hasSignupBonus

if (import.meta.client) {
    watch(() => route.path, () => {
        refreshFlashMessages()
        if (isLoggedIn.value) {
            pendingReward.refresh()
            pendingBonus.refresh()
        }
        if (isEligibleForAccountCompletion.value) {
            signupBonusOffers.refresh()
        }
    })
}

provide(LAYOUT_CONTEXT_KEY, {
    hero,
    personalizedHeroGame,
    personalizedGamesSection: personalizedGamesSectionQuery.data,
    personalizedGamesSectionLoading: personalizedGamesSectionQuery.isLoading,
    fetchPersonalizedContent,
    personalizedContentVisible,
})
</script>

<template>
    <div class="default">
        <UILayout v-bind="layoutProps" :has-self-assessment="flags?.enableSelfAssessment ?? false">
            <template #header>
                <SelfAssessmentContainer v-if="flags?.enableSelfAssessment" />
                <NavigationContainer />
            </template>

            <template #promotions>
                <Notifications />
                <LazyNimbusNotificationFlashContainer />
                <LazyFlashMessages v-if="hasFlashMessages" :messages="filteredFlashMessages" />

                <template v-if="!route.meta.hidePromotions">
                    <LazyPromotionNextCounter v-if="reclaimableReminder" :reclaimable-reminder="reclaimableReminder" />
                    <LazyPromotionContainer v-if="flags?.enablePromotions" />
                </template>
            </template>

            <template v-if="!route.meta.hidePromotions && !pendingBonus.data.value && hasSignupBonus !== undefined" #accountCompletion>
                <LazyNimbusAccountCompletion :show-bonus="hasSignupBonus" />
            </template>

            <template v-if="showHero" #hero>
                <NimbusHeroContainer
                    v-if="heroDisplayMode === 'service'"
                    :data="hero.data.value"
                    :preset="imagePreset"
                    @game-click="game => trackGameOpen('hero', game)"
                />
                <NimbusHeroPersonalized
                    v-else-if="heroDisplayMode === 'personalized'"
                    :game="personalizedHeroGame"
                    :preset="imagePreset"
                    @game-click="game => trackGameOpen('hero', game)"
                />
            </template>

            <template v-if="showStickyTerms" #terms>
                <StickyTerms
                    :hero-data="hero.data.value"
                    :sticky-config="{ container: '.layout__container', minHeight: 48, offset: 50 }"
                />
            </template>

            <template v-if="route.meta?.sidebar" #aside>
                <LazySidebarContainer />
            </template>

            <template #main>
                <slot />
            </template>

            <template #footer>
                <FooterContainer />
            </template>
        </UILayout>
    </div>
</template>
